import { oc } from "@orpc/contract";
import {
  SheetNameInputSchema,
  WorkbookSheetsResponseFields,
} from "./workbook-preview.contract";
import { z } from "zod";

/**
 * Customer Product Match DTO Schema
 */
export const CustomerProductMatchSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  productId: z.uuid(),
  productVariantId: z.uuid(),

  // Customer identifier fields
  customerDescription: z.string().nullable(),
  customerMainIdentifier: z.string().nullable(),
  customerSku: z.string().nullable(),
  customerUpc: z.string().nullable(),
  customerPlu: z.string().nullable(),
  customerEan: z.string().nullable(),
  customerGtin: z.string().nullable(),
  customerAsin: z.string().nullable(),
  customerMpn: z.string().nullable(),
  customerIsbn: z.string().nullable(),
  customerNsn: z.string().nullable(),
  customerFreeform: z.array(z.string()).nullable(),

  customer: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  product: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  productVariant: z.object({
    id: z.uuid(),
    name: z.string(),
    sku: z.string().nullable(),
    active: z.boolean(),
  }),

  matchSequence: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List Customer Product Matches Query Schema
 */
export const ListCustomerProductMatchesQuerySchema = z.object({
  customerId: z.uuid().optional(),
  productId: z.uuid().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).prefault(25),
  offset: z.coerce.number().int().min(0).prefault(0),
});

/**
 * List Customer Product Matches Response Schema
 */
export const ListCustomerProductMatchesResponseSchema = z.object({
  customerProductMatches: z.array(CustomerProductMatchSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

/**
 * Shared optional fields for customer product updates
 */
const CustomerProductOptionalFieldsSchema = z.object({
  customerDescription: z.string().nullable().optional(),
  customerMainIdentifier: z.string().nullable().optional(),
  customerSku: z.string().nullable().optional(),
  customerUpc: z.string().nullable().optional(),
  customerPlu: z.string().nullable().optional(),
  customerEan: z.string().nullable().optional(),
  customerGtin: z.string().nullable().optional(),
  customerAsin: z.string().nullable().optional(),
  customerMpn: z.string().nullable().optional(),
  customerIsbn: z.string().nullable().optional(),
  customerNsn: z.string().nullable().optional(),
  matchSequence: z.number().int().min(0).optional(),
});

/**
 * Create Customer Product input schema
 */
export const CreateCustomerProductInputSchema =
  CustomerProductOptionalFieldsSchema.extend({
    customerId: z.uuid(),
    productVariantId: z.uuid(),
    customerFreeform: z.array(z.string()).min(1),
  });

/**
 * Create Customer Product response schema
 */
export const CreateCustomerProductResponseSchema = z.object({
  customerProductId: z.uuid(),
  didCreate: z.boolean(),
});

/**
 * Update Customer Product input schema
 */
export const UpdateCustomerProductInputSchema =
  CustomerProductOptionalFieldsSchema.extend({
    customerProductId: z.uuid(),
    productVariantId: z.uuid().optional(),
    customerFreeform: z.array(z.string()).nullable().optional(),
  });

/**
 * Update Customer Product response schema
 */
export const UpdateCustomerProductResponseSchema = z.object({
  customerProductId: z.uuid(),
});

/**
 * Delete Customer Product input schema
 */
export const DeleteCustomerProductInputSchema = z.object({
  customerProductId: z.uuid(),
});

/**
 * Delete Customer Product response schema
 */
export const DeleteCustomerProductResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Bulk upload (phase A): preview an uploaded workbook's headers + sample rows.
 */
export const PreviewCustomerProductUploadInputSchema = z.object({
  documentId: z.uuid(),
  sheetName: SheetNameInputSchema,
});

export const PreviewCustomerProductUploadResponseSchema = z.object({
  ...WorkbookSheetsResponseFields,
  headers: z.array(z.string()),
  sampleRows: z.array(z.array(z.string())),
  totalDataRows: z.number().int().min(0),
  suggestedCustomerPartColumn: z.number().int().min(0).nullable(),
  suggestedInternalPartColumn: z.number().int().min(0).nullable(),
});

/**
 * Bulk upload (phase B): ingest the picked columns as customer → variant
 * mappings (customer part number stored as a customerFreeform term, internal
 * part number matched to a product variant SKU).
 */
export const BulkUploadCustomerProductsInputSchema = z.object({
  customerId: z.uuid(),
  documentId: z.uuid(),
  sheetName: SheetNameInputSchema,
  customerPartColumnIndex: z.number().int().min(0),
  internalPartColumnIndex: z.number().int().min(0),
});

export const UnmatchedCustomerProductRowSchema = z.object({
  rowIndex: z.number().int(),
  customerPart: z.string(),
  internalPart: z.string(),
});

export const BulkUploadCustomerProductsResponseSchema = z.object({
  totalRows: z.number().int().min(0),
  createdCount: z.number().int().min(0),
  mergedCount: z.number().int().min(0),
  unmatchedCount: z.number().int().min(0),
  skippedRows: z.number().int().min(0),
  /** Capped list — see `unmatchedTruncated`; `unmatchedCount` is exact. */
  unmatched: z.array(UnmatchedCustomerProductRowSchema),
  unmatchedTruncated: z.boolean(),
});

/**
 * Customer Products Contracts
 */
export const customerProductsContracts = {
  list: oc
    .input(ListCustomerProductMatchesQuerySchema)
    .output(ListCustomerProductMatchesResponseSchema)
    .route({
      summary: "List customer product mappings",
      description:
        "Lists mappings from customers' part numbers and descriptions to internal products, filterable by customer or product and searchable by name, paginated.",
      method: "GET",
      path: "/customer-products",
    }),
  create: oc
    .input(CreateCustomerProductInputSchema)
    .output(CreateCustomerProductResponseSchema)
    .route({
      summary: "Map a customer part number to a product",
      description:
        "Creates or merges a customer's part numbers into a mapping to a product variant, so their orders auto-match; removes those part numbers from the customer's other mappings.",
      method: "POST",
      path: "/customer-products",
    }),
  update: oc
    .input(UpdateCustomerProductInputSchema)
    .output(UpdateCustomerProductResponseSchema)
    .route({
      summary: "Update a customer product mapping",
      description:
        "Changes a customer product mapping's product variant, customer identifiers (SKU, UPC, GTIN, part numbers, description), or match priority.",
      method: "PUT",
      path: "/customer-products",
    }),
  delete: oc
    .input(DeleteCustomerProductInputSchema)
    .output(DeleteCustomerProductResponseSchema)
    .route({
      summary: "Delete a customer product mapping",
      description:
        "Removes a mapping between a customer's part number and an internal product, so it no longer auto-matches on their orders.",
      method: "DELETE",
      path: "/customer-products",
    }),
  previewBulkUpload: oc
    .input(PreviewCustomerProductUploadInputSchema)
    .output(PreviewCustomerProductUploadResponseSchema)
    .route({
      summary: "Preview a customer part number spreadsheet",
      description:
        "Reads an uploaded spreadsheet sheet and returns headers, sample rows, row count, and suggested customer-part and internal-part columns before a bulk import. Read-only.",
      method: "POST",
      path: "/customer-products/preview-upload",
    }),
  bulkUpload: oc
    .input(BulkUploadCustomerProductsInputSchema)
    .output(BulkUploadCustomerProductsResponseSchema)
    .route({
      summary: "Import customer part numbers from a spreadsheet",
      description:
        "Creates or merges customer-to-product mappings from the chosen columns of an uploaded sheet, matching internal part numbers to SKUs; returns counts and unmatched rows.",
      method: "POST",
      path: "/customer-products/bulk-upload",
    }),
} as const;

// Export types
export type CustomerProductMatch = z.infer<typeof CustomerProductMatchSchema>;
export type ListCustomerProductMatchesQuery = z.infer<
  typeof ListCustomerProductMatchesQuerySchema
>;
export type ListCustomerProductMatchesResponse = z.infer<
  typeof ListCustomerProductMatchesResponseSchema
>;
export type CreateCustomerProductInput = z.infer<
  typeof CreateCustomerProductInputSchema
>;
export type CreateCustomerProductResponse = z.infer<
  typeof CreateCustomerProductResponseSchema
>;
export type UpdateCustomerProductInput = z.infer<
  typeof UpdateCustomerProductInputSchema
>;
export type UpdateCustomerProductResponse = z.infer<
  typeof UpdateCustomerProductResponseSchema
>;
export type DeleteCustomerProductInput = z.infer<
  typeof DeleteCustomerProductInputSchema
>;
export type DeleteCustomerProductResponse = z.infer<
  typeof DeleteCustomerProductResponseSchema
>;
export type PreviewCustomerProductUploadInput = z.infer<
  typeof PreviewCustomerProductUploadInputSchema
>;
export type PreviewCustomerProductUploadResponse = z.infer<
  typeof PreviewCustomerProductUploadResponseSchema
>;
export type BulkUploadCustomerProductsInput = z.infer<
  typeof BulkUploadCustomerProductsInputSchema
>;
export type BulkUploadCustomerProductsResponse = z.infer<
  typeof BulkUploadCustomerProductsResponseSchema
>;
export type UnmatchedCustomerProductRow = z.infer<
  typeof UnmatchedCustomerProductRowSchema
>;
